import "@fullcalendar/react/dist/vdom"; // necessary for correct rendering order of calendar and plugins

import { faEllipsisH } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayGridPlugin from "@fullcalendar/daygrid"; // Plugin
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import { EventContentArg } from "@fullcalendar/react";
import FullCalendar from "@fullcalendar/react"; // Core FullCalendar library
import timeGridPlugin from "@fullcalendar/timegrid";
import { Grid } from "@mui/material";

import { Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useMemo, useEffect } from "react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import ViewRemainingAbsencesModal from "src/containers/MyTeamPresenceAndLeaves/AllEmployeeLeaves/AllEmployeeLeavesViews/ViewAllAbsencesModal/ViewAllAbsencesModal";
import { Props, EmployeeData } from "./interfaces";
import { FullCalendarContainer, Container, DayDate, DayHeaderContainer } from "./styles";
import { useAppSelector } from "../../../../custom-hooks/redux/redux";
import { isAuthorized } from "../../../../custom-hooks/useAuthorization/useAuthorization";
import { CustomDate } from "../../../../models/custom-date/custom-date";
import { TnAConfigRef } from "../../../../models/tna";
import { TnAAbsenceDTO } from "../../../../models/tna/tna-absences/tna-absence-dto";
import { TnAClockingRef } from "../../../../models/tna/tna-clockings/tna-clocking-ref";
import { TnADayDataDTO } from "../../../../models/tna/tna-data/tna-day-data-dto";
import SimplePopover from "../../../../reusable-components/SimplePopover/SimplePopover";
import { AUTHORIZATIONS } from "../../../../utils/constants/authorization-constants";

import { computeMonthName } from "../../../../utils/time-utils";
import AbsenceButton from "../../../MyCalendar/AddEditAbsence/AbsenceButton";
import EditAbsenceWrapper from "../../../MyCalendar/AddEditAbsence/EditAbsenceWrapper/EditAbsenceWrapper";
import { AbsenceStatusIndicator } from "../../../MyCalendar/AddEditAbsence/styles";

export class CalendarEvent {
  id = "";
  title = "";
  allDay = false;
  start: Date = new Date();
  end: Date = new Date();
  type = "";
  item: TnADayDataDTO | TnAAbsenceDTO | TnAClockingRef[] | null = null;
  tnaConfigForThisDay: TnAConfigRef | null = null;
}

export interface State {
  dayData: TnADayDataDTO[];
}

const AllEmployeeLeavesCalendar = ({
  year,
  month,
  notifyParent,
  forManager = true,
  viewMode = false,
  absences, // Direct absences data - preferred approach
  allEmployeesSelected = false,
  allowedOnlyApproveReject = false,
  minDate,
  maxDate,
}: Props) => {
  const { i18n, t } = useTranslation();
  const theme = useTheme();

  // const currCompanyAccount = useAppSelector((state) => state.currentUserReducer.currentCompanyAccount);

  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  //#region allow to approve reject
  const allowedToApproveReject = useMemo(() => {
    return isAuthorized([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves], currentUserAuthorizations);
  }, [currentUserAuthorizations]);
  //endregion

  //#region create absences events
  const absenceEvents: CalendarEvent[] = useMemo(() => {
    const events: CalendarEvent[] = [];

    // Use absences prop directly - this contains all necessary data from TnAAbsenceDTO
    if (absences && absences.length > 0) {
      const absenceCalendarEvents = absences.map(
        (absence) =>
          ({
            id: `${absence.employee?.id || "unknown"}-${absence.id}`,
            title: `${absence.employee?.firstName || ""} ${absence.employee?.lastName || ""}: ${
              absence.absenceType?.name
            }`,
            allDay: false,
            start: CustomDate.ToDate(absence.fromDate),
            end: CustomDate.ToEndOfDate(absence.toDate),
            type: "absence",
            item: absence,
            tnaConfigForThisDay: null,
            employeeId: absence.employee?.id || "unknown",
            employeeName: `${absence.employee?.firstName || ""} ${absence.employee?.lastName || ""}`,
          } as CalendarEvent & { employeeId: string; employeeName: string })
      );
      events.push(...absenceCalendarEvents);
    }

    return events;
  }, [absences]);

  // Group absences by date for overflow management
  const absencesByDate = useMemo(() => {
    const groupedAbsences: { [key: string]: TnAAbsenceDTO[] } = {};

    if (absences && absences.length > 0) {
      absences.forEach((absence) => {
        const startDate = CustomDate.ToDate(absence.fromDate);
        if (startDate) {
          const dateKey = `${startDate.getFullYear()}-${(startDate.getMonth() + 1)
            .toString()
            .padStart(2, "0")}-${startDate.getDate().toString().padStart(2, "0")}`;
          if (!groupedAbsences[dateKey]) {
            groupedAbsences[dateKey] = [];
          }
          groupedAbsences[dateKey].push(absence);
        }
      });
    }

    return groupedAbsences;
  }, [absences]);
  //#endregion

  //#region events
  const events = absenceEvents;
  //#endregion

  //#region weekend styling effect
  useEffect(() => {
    const datesAreWeekend: { [key: string]: boolean } = {};

    // Generate all dates for the current month to cover the calendar view
    //  const startOfYear = new Date(year, 0, 1);
    // const endOfYear = new Date(year, 11, 31); // Last day of year
    const startOfMonth = new Date(year, month, 1);
    const endOfMonth = new Date(year, month, 31); // Last day of current month

    // Generate all dates in the current month
    const currentDate = new Date(startOfMonth);
    while (currentDate <= endOfMonth) {
      const isWeekend = currentDate.getDay() === 0 || currentDate.getDay() === 6;

      if (isWeekend) {
        const currDate = `${currentDate.getFullYear()}-${
          currentDate.getMonth() + 1 < 10 ? `0${currentDate.getMonth() + 1}` : currentDate.getMonth() + 1
        }-${currentDate.getDate() < 10 ? `0${currentDate.getDate()}` : currentDate.getDate()}`;

        datesAreWeekend[currDate] = true;
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Apply weekend styling to all calendar cells
    const tags = document.querySelectorAll("[data-date]");

    if (!tags.length) return;

    const dayCellClass = "dayCellClass";
    const isWeekendClass = "isWeekend";

    tags.forEach((tag) => {
      if (tag) {
        const tagDate = (tag as HTMLElement).dataset.date;

        if (!tagDate) return;

        // Remove existing classes first
        tag.classList.remove(dayCellClass, isWeekendClass);

        if (datesAreWeekend[tagDate]) {
          tag.classList.add(dayCellClass);
          tag.classList.add(isWeekendClass);

          if (tag?.children) {
            for (let i = 0; i < tag.children.length; i++) {
              tag.children[i]?.classList.remove(dayCellClass, isWeekendClass);
              tag.children[i]?.classList.add(dayCellClass);
              tag.children[i]?.classList.add(isWeekendClass);
            }
          }
        }
      }
    });
  }, [year, month]);
  //#endregion

  //#region render day cell content
  const renderDayCellContent = (date: Date): ReactNode => {
    return (
      <DayHeaderContainer>
        <DayDate>
          <Typography>
            {computeMonthName(date.getMonth()).slice(0, 3)}&nbsp;
            {date.getDate()}
            {date.getFullYear() === new Date().getFullYear() &&
              date.getMonth() === new Date().getMonth() &&
              date.getDate() === new Date().getDate() &&
              " (Today)"}
          </Typography>
        </DayDate>
      </DayHeaderContainer>
    );
  };
  //#endregion

  //#region render absence
  const renderAbsense = (absence: TnAAbsenceDTO, employeeData: EmployeeData) => {
    const employee = absence.employee;
    const paidLeaves = employeeData?.paidLeaves || {};

    return (
      <AbsenceStatusIndicator
        isRejected={absence.isRejected}
        isCancelled={absence.isCancelled}
        isApproved={absence.isApproved}
        key={absence.id}
      >
        <EditAbsenceWrapper
          absence={absence}
          employee={employee}
          handleNotifyParent={notifyParent}
          forManager
          allowedToApproveReject={allowedToApproveReject}
          minDate={minDate}
          maxDate={maxDate}
          viewMode={viewMode || allEmployeesSelected}
          allowedOnlyApproveReject={allowedOnlyApproveReject}
          showAdditionalInfoMessage={allEmployeesSelected}
          additionalInfoMessage={
            allEmployeesSelected ? t("components.my_calendar.add_absence.info_message") : undefined
          }
        >
          <AbsenceButton
            absence={absence}
            forManager
            paidLeaves={paidLeaves}
          />
          <Typography
            sx={{ width: "100%" }}
            variant="body2"
          >{`(${employee?.name})`}</Typography>
        </EditAbsenceWrapper>
      </AbsenceStatusIndicator>
    );
  };
  //#endregion

  //#region render event content
  const renderEventContent = (eventInfo: EventContentArg) => {
    const type = eventInfo.event._def.extendedProps.type;
    if (type === "absence") {
      const absence = eventInfo.event._def.extendedProps.item as TnAAbsenceDTO;
      const eventDate = eventInfo.event.start;

      if (!eventDate) return false;

      // Get date key for this event
      const dateKey = `${eventDate.getFullYear()}-${(eventDate.getMonth() + 1).toString().padStart(2, "0")}-${eventDate
        .getDate()
        .toString()
        .padStart(2, "0")}`;
      const dayAbsences = absencesByDate[dateKey] || [];
      const absenceIndex = dayAbsences.findIndex((a) => a.id === absence.id);

      // Create minimal employeeData since we don't have the full employeesData structure
      const employeeData: EmployeeData = {
        tnAActualConfig: null,
        tnAConfigsForThatPeriod: [],
        tnAData: null,
        todayTnAConfig: null,
        paidLeaves: {},
      };

      // Simple display logic - show unless explicitly cancelled/rejected
      const shouldDisplay = !absence.isRejected && !absence.isCancelled;

      if (!shouldDisplay) return false;

      // Show first 3 absences directly
      if (absenceIndex < 3) {
        return renderAbsense(absence, employeeData);
      }

      // Show ellipsis button for 4th absence (representing all remaining)
      if (absenceIndex === 3) {
        const remainingAbsences = dayAbsences.slice(3); // Only absences from 4th onwards for popover
        return (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "2px" }}>
            <ViewRemainingAbsencesModal>
              {dayAbsences.map((dayAbsence) => (
                <div key={dayAbsence.id}>{renderAbsense(dayAbsence, employeeData)}</div>
              ))}
            </ViewRemainingAbsencesModal>
            <SimplePopover
              buttonType="icon"
              buttonContent={
                <FontAwesomeIcon
                  icon={faEllipsisH}
                  style={{ width: "16px", height: "16px" }}
                />
              }
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              customButtonStyle={{
                color: theme.palette.primary.main,
                padding: "8px",
                minWidth: "auto",
              }}
            >
              <Grid sx={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: "200px" }}>
                {remainingAbsences.map((remainingAbsence) => (
                  <div key={remainingAbsence.id}>{renderAbsense(remainingAbsence, employeeData)}</div>
                ))}
              </Grid>
            </SimplePopover>
          </div>
        );
      }

      // Don't render absences beyond the 4th one (they're shown in popover)
      return false;
    }
    return false;
  };
  //#endregion

  return (
    <Container>
      <FullCalendarContainer id="full-calendar-container">
        <FullCalendar
          height={"auto"}
          eventOrderStrict
          weekNumberCalculation={"ISO"}
          displayEventEnd={true}
          initialDate={new Date(year, month, 1)}
          locale={i18n.language}
          plugins={[dayGridPlugin, interactionPlugin, listPlugin, timeGridPlugin]}
          initialView={"dayGridMonth"}
          events={events}
          weekNumbers
          weekends
          nowIndicator={false}
          eventContent={renderEventContent}
          // datesSet={(dates) => {
          //   setCurrentDates(dates);
          // }}
          headerToolbar={{
            start: "",
            center: "",
            end: "",
          }}
          eventColor={"transparent"}
          titleFormat={{
            year: "numeric",
            month: "long",
            day: "2-digit",
          }}
          eventDisplay={"center"}
          views={{
            dayGridMonth: {
              // name of view
              titleFormat: {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              },
            },
          }}
          dayCellContent={(arg) => renderDayCellContent(arg.date)}
        />
      </FullCalendarContainer>
    </Container>
  );
};

export default React.memo(AllEmployeeLeavesCalendar);
